import { useState, useEffect, useRef, useCallback } from "react";

// ─── Config ───────────────────────────────────────────────────────────────────
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// include Google Tasks scope for task integration
const SCOPES = "https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/tasks";
const LS = (k, fallback) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fallback; } catch { return fallback; } };
const LSset = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { } };
const APP_VERSION = process.env.REACT_APP_VERSION || "0.1.0";
const UPDATE_CHECK_INTERVAL = 1000 * 60 * 60 * 4; // every 4 hours

// ─── Helpers ──────────────────────────────────────────────────────────────────
function startOfWeek(date) {
  const d = new Date(date); const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day)); d.setHours(0, 0, 0, 0); return d;
}
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function formatDate(date) { return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
function formatShort(date) { return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
function formatTime(iso) { if (!iso) return ""; return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
function isSameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function toDateInput(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// ─── Themes ───────────────────────────────────────────────────────────────────
const THEMES = [
  { id: "tan", name: "Tan", leatherDark: "#2A1A0E", leatherMid: "#4A2E1A", leatherLight: "#6B4226", leatherTan: "#8B5E3C", ringGold: "#C8A84B" },
  { id: "grey", name: "Slate Grey", leatherDark: "#1C1C22", leatherMid: "#2E2E38", leatherLight: "#48484E", leatherTan: "#8A8A96", ringGold: "#C0C0C8" },
  { id: "navy", name: "Navy", leatherDark: "#0A0E1A", leatherMid: "#16223A", leatherLight: "#1E3558", leatherTan: "#4A6A9A", ringGold: "#C8A84B" },
  { id: "forest", name: "Forest", leatherDark: "#0A180E", leatherMid: "#142A18", leatherLight: "#1E4226", leatherTan: "#3A6A42", ringGold: "#B8A84B" },
  { id: "pink", name: "Blush Pink", leatherDark: "#2A0A14", leatherMid: "#4A1828", leatherLight: "#7A2848", leatherTan: "#C06080", ringGold: "#E8A0B8" },
  { id: "burgundy", name: "Burgundy", leatherDark: "#1A0808", leatherMid: "#3A0E0E", leatherLight: "#5A1A1A", leatherTan: "#8A3030", ringGold: "#C8A84B" },
  { id: "camel", name: "Camel", leatherDark: "#8C6A48", leatherMid: "#A68662", leatherLight: "#C4A785", leatherTan: "#D4BBA1", ringGold: "#F4D03F" },
  { id: "skyblue", name: "Sky Blue", leatherDark: "#4A7A9A", leatherMid: "#6494B4", leatherLight: "#84B4D4", leatherTan: "#A4D0EE", ringGold: "#FFDF00" },
  { id: "sage", name: "Sage Green", leatherDark: "#5A7A5A", leatherMid: "#749474", leatherLight: "#94B494", leatherTan: "#B0D0B0", ringGold: "#FFD700" },
  { id: "coral", name: "Coral", leatherDark: "#9A4A4A", leatherMid: "#B46464", leatherLight: "#D48484", leatherTan: "#EEA4A4", ringGold: "#FFC233" },
  { id: "lavender", name: "Lavender", leatherDark: "#6A5A8A", leatherMid: "#8474A4", leatherLight: "#A494C4", leatherTan: "#C0B4E0", ringGold: "#FCE883" },
  { id: "yellow", name: "Honey Yellow", leatherDark: "#2B2100", leatherMid: "#4D3B00", leatherLight: "#735800", leatherTan: "#A68000", ringGold: "#E8C547" },
];

const PAPER_STYLES = [
  { id: "cream", name: "Cream", bg: "#F5F0E8", warm: "#EDE5D0", aged: "#E8DCC8" },
  { id: "white", name: "White", bg: "#FAFAF8", warm: "#F4F4F0", aged: "#EEEEE8" },
];

const FONTS = [
  { id: "garamond", name: "EB Garamond", import: "EB+Garamond:ital,wght@0,400;0,500;1,400", family: "'EB Garamond', Georgia, serif" },
  { id: "playfair", name: "Playfair Display", import: "Playfair+Display:ital,wght@0,400;0,600;1,400", family: "'Playfair Display', Georgia, serif" },
  { id: "cormorant", name: "Cormorant Garamond", import: "Cormorant+Garamond:ital,wght@0,300;0,400;1,300", family: "'Cormorant Garamond', Georgia, serif" },
  { id: "lora", name: "Lora", import: "Lora:ital,wght@0,400;0,600;1,400", family: "'Lora', Georgia, serif" },
  { id: "crimson", name: "Crimson Text", import: "Crimson+Text:ital,wght@0,400;0,600;1,400", family: "'Crimson Text', Georgia, serif" },
];

// ─── Default colour rules ─────────────────────────────────────────────────────
const DEFAULT_COLOR_RULES = [
  { keyword: "birthday", color: "#C0392B" }, { keyword: "dentist", color: "#8E44AD" },
  { keyword: "doctor", color: "#1A7A4A" }, { keyword: "hospital", color: "#1A7A4A" },
  { keyword: "holiday", color: "#2471A3" }, { keyword: "flight", color: "#2471A3" },
  { keyword: "meeting", color: "#784212" }, { keyword: "lunch", color: "#B7950B" },
  { keyword: "dinner", color: "#7D6608" }, { keyword: "gym", color: "#1E8449" },
  { keyword: "exam", color: "#6E2F1A" }, { keyword: "school", color: "#1A5276" },
  { keyword: "wedding", color: "#C0392B" },
];

function getEventColor(title, rules) {
  if (!title) return "#7A6B5A";
  const lower = title.toLowerCase();
  for (const r of rules) { if (lower.includes(r.keyword.toLowerCase())) return r.color; }
  return "#7A6B5A";
}

// ─── Event icons ──────────────────────────────────────────────────────────────
const ICON_RULES = [
  { keywords: ["birthday", "bday"], icon: "🎈" },
  { keywords: ["dentist", "dental", "tooth"], icon: "🦷" },
  { keywords: ["doctor", "gp", "hospital", "clinic", "surgery", "consultant", "nhs"], icon: "🏥" },
  { keywords: ["holiday", "vacation", "beach", "resort"], icon: "🌴" },
  { keywords: ["flight", "airport", "flying", "travel", "plane"], icon: "✈️" },
  { keywords: ["train", "rail", "station"], icon: "🚂" },
  { keywords: ["exam", "test", "assessment", "gcse", "alevel", "a-level"], icon: "📝" },
  { keywords: ["wedding", "anniversary"], icon: "💍" },
  { keywords: ["dinner", "restaurant", "meal"], icon: "🍽️" },
  { keywords: ["lunch", "brunch"], icon: "🥗" },
  { keywords: ["gym", "workout", "fitness", "run", "running", "yoga", "pilates"], icon: "💪" },
  { keywords: ["party", "celebration", "drinks"], icon: "🥂" },
  { keywords: ["school", "college", "university", "lecture", "class"], icon: "🎓" },
  { keywords: ["meeting", "conference", "call", "zoom", "teams"], icon: "💼" },
  { keywords: ["cinema", "film", "movie", "theatre", "show", "concert", "gig"], icon: "🎭" },
  { keywords: ["car", "mot", "service", "garage", "mechanic"], icon: "🚗" },
  { keywords: ["haircut", "hairdresser", "barber", "salon"], icon: "✂️" },
  { keywords: ["golf"], icon: "⛳" },
  { keywords: ["football", "rugby", "cricket", "tennis", "swimming"], icon: "🏅" },
  { keywords: ["kids", "children", "child", "nursery", "playdate"], icon: "👶" },
  { keywords: ["date night", "date"], icon: "❤️" },
  { keywords: ["move", "moving", "removal"], icon: "📦" },
  { keywords: ["interview", "job"], icon: "🤝" },
  { keywords: ["pay", "salary", "invoice", "tax", "accountant"], icon: "💷" },
];
function getEventIcon(title) {
  if (!title) return null;
  const lower = title.toLowerCase();
  for (const r of ICON_RULES) { if (r.keywords.some(k => lower.includes(k))) return r.icon; }
  return null;
}

// ─── Smart text parser ────────────────────────────────────────────────────────
const UK_POSTCODE_RE = /\b([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})\b/i;
const PHONE_RE = /(\+44\s?[\d\s]{10,}|0[\d\s\-]{9,13})/;
const EMAIL_RE = /([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/;

function extractTimeFromText(text) {
  const match = text.match(/\b(\d{1,2}):(\d{2})\s*(am|pm)?\b|\b(\d{1,2})\s*(am|pm)\b/i);
  if (!match) return null;

  let hour = 0;
  let minute = 0;
  let raw = match[0];

  if (match[1] !== undefined) {
    hour = parseInt(match[1], 10);
    minute = parseInt(match[2], 10);
    const ampm = match[3];
    if (ampm) {
      if (ampm.toLowerCase() === "pm" && hour < 12) hour += 12;
      if (ampm.toLowerCase() === "am" && hour === 12) hour = 0;
    }
  } else if (match[4] !== undefined) {
    hour = parseInt(match[4], 10);
    minute = 0;
    const ampm = match[5];
    if (ampm) {
      if (ampm.toLowerCase() === "pm" && hour < 12) hour += 12;
      if (ampm.toLowerCase() === "am" && hour === 12) hour = 0;
    }
  }

  if (hour >= 0 && hour < 24 && minute >= 0 && minute < 60) {
    return { hour, minute, raw };
  }
  return null;
}

function localParseVoice(text) {
  const result = {
    title: text,
    date: null,
    startTime: null,
    endTime: null,
    allDay: false
  };

  const lower = text.toLowerCase();
  const timeParsed = extractTimeFromText(text);
  if (timeParsed) {
    const hh = String(timeParsed.hour).padStart(2, "0");
    const mm = String(timeParsed.minute).padStart(2, "0");
    result.startTime = `${hh}:${mm}`;
    let endHours = timeParsed.hour + 1;
    if (endHours >= 24) endHours = 23;
    result.endTime = `${String(endHours).padStart(2, "0")}:${mm}`;
  }

  const today = new Date();
  if (lower.includes("tomorrow")) {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    result.date = tomorrow.toISOString().split("T")[0];
  } else if (lower.includes("today")) {
    result.date = today.toISOString().split("T")[0];
  } else {
    const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    for (let i = 0; i < 7; i++) {
      if (lower.includes(daysOfWeek[i])) {
        const targetDay = i;
        const currentDay = today.getDay();
        let diff = targetDay - currentDay;
        if (diff <= 0) diff += 7;
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + diff);
        result.date = targetDate.toISOString().split("T")[0];
        break;
      }
    }
  }

  let cleanTitle = text
    .replace(/\b(tomorrow|today|yesterday)\b/gi, "")
    .replace(/\b(on)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi, "")
    .replace(/\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi, "")
    .replace(/\b(?:at|@\s*)?\d{1,2}(?::\d{2})?\s*(?:am|pm)?\b/gi, "")
    .replace(/\b(?:at|@\s*)\d{1,2}:\d{2}\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  if (cleanTitle) {
    cleanTitle = cleanTitle.charAt(0).toUpperCase() + cleanTitle.slice(1);
    result.title = cleanTitle;
  }

  return result;
}

function parseEventText(text) {
  if (!text) return { clean: "", phone: null, email: null, postcode: null, time: null };
  let clean = text, phone = null, email = null, postcode = null, time = null;
  const em = clean.match(EMAIL_RE); if (em) { email = em[1]; clean = clean.replace(em[1], "").trim(); }
  const pm = clean.match(PHONE_RE); if (pm) { phone = pm[1].trim(); clean = clean.replace(pm[1], "").trim(); }
  const pc = clean.match(UK_POSTCODE_RE); if (pc) postcode = pc[1].trim();
  time = extractTimeFromText(clean);
  clean = clean.replace(/\s{2,}/g, " ").replace(/[,·\-]\s*$/, "").trim();
  return { clean, phone, email, postcode, time };
}


// ─── Main App ─────────────────────────────────────────────────────────────────
export default function FlofaxPlanner() {
  const [gapiReady, setGapiReady] = useState(false);
  const [gisReady, setGisReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date()));
  const [pageIndex, setPageIndex] = useState(0);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [swipeDir, setSwipeDir] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [showEventsList, setShowEventsList] = useState(false);
  const [eventsListRange, setEventsListRange] = useState(null);
  const [eventsList, setEventsList] = useState([]);
  const [showTasks, setShowTasks] = useState(false);
  const [showWeekDetails, setShowWeekDetails] = useState(false);
  const [weekDetailsEvents, setWeekDetailsEvents] = useState([]);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState(null);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [offlineMode, setOfflineMode] = useState(() => typeof navigator !== "undefined" ? !navigator.onLine : false);
  const [lastSignedIn, setLastSignedInState] = useState(() => LS("flofax_lastSignedIn", false));
  const [cachedTaskData, setCachedTaskData] = useState(() => LS("flofax_cachedTasks", { lists: [], activeListId: null, tasksByList: {} }));

  // Persisted settings
  const [colorRules, setColorRulesState] = useState(() => LS("flofax_colorRules", DEFAULT_COLOR_RULES));
  const [themeId, setThemeIdState] = useState(() => LS("flofax_theme", "tan"));
  const [paperId, setPaperIdState] = useState(() => LS("flofax_paper", "cream"));
  const [fontId, setFontIdState] = useState(() => LS("flofax_font", "garamond"));
  const [morningNotificationEnabled, setMorningNotificationEnabledState] = useState(() => LS("flofax_morningNotificationEnabled", false));
  const [morningNotificationTime, setMorningNotificationTimeState] = useState(() => LS("flofax_morningNotificationTime", "08:00"));
  const [bedtimeNotificationEnabled, setBedtimeNotificationEnabledState] = useState(() => LS("flofax_bedtimeNotificationEnabled", false));
  const [bedtimeNotificationTime, setBedtimeNotificationTimeState] = useState(() => LS("flofax_bedtimeNotificationTime", "21:00"));
  const [notificationPermission, setNotificationPermission] = useState("default");
  const notificationTimers = useRef([]);

  const setColorRules = v => { setColorRulesState(v); LSset("flofax_colorRules", v); };
  const setThemeId = v => { setThemeIdState(v); LSset("flofax_theme", v); };
  const setPaperId = v => { setPaperIdState(v); LSset("flofax_paper", v); };
  const setFontId = v => { setFontIdState(v); LSset("flofax_font", v); };
  const setMorningNotificationEnabled = v => { setMorningNotificationEnabledState(v); LSset("flofax_morningNotificationEnabled", v); };
  const setMorningNotificationTime = v => { setMorningNotificationTimeState(v); LSset("flofax_morningNotificationTime", v); };
  const setBedtimeNotificationEnabled = v => { setBedtimeNotificationEnabledState(v); LSset("flofax_bedtimeNotificationEnabled", v); };
  const setBedtimeNotificationTime = v => { setBedtimeNotificationTimeState(v); LSset("flofax_bedtimeNotificationTime", v); };

  const theme = THEMES.find(t => t.id === themeId) || THEMES[0];
  const paper = PAPER_STYLES.find(p => p.id === paperId) || PAPER_STYLES[0];
  const font = FONTS.find(f => f.id === fontId) || FONTS[0];
  const hasCachedEvents = !!LS("flofax_cachedEvents", []).length;

  // Modals
  const [showSettings, setShowSettings] = useState(false);
  const [addModal, setAddModal] = useState(null); // { day }
  const [editModal, setEditModal] = useState(null); // { event }

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const isLandscapeRef = useRef(isLandscape);
  useEffect(() => { isLandscapeRef.current = isLandscape; }, [isLandscape]);

  // Orientation
  useEffect(() => {
    const check = () => setIsLandscape(window.innerWidth > window.innerHeight);
    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return () => { window.removeEventListener("resize", check); window.removeEventListener("orientationchange", check); };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateOnline = () => setOfflineMode(!navigator.onLine);
    updateOnline();
    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);
    return () => { window.removeEventListener("online", updateOnline); window.removeEventListener("offline", updateOnline); };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!navigator.onLine) {
      const cached = LS("flofax_cachedEvents", []);
      if (cached.length) {
        setEvents(cached);
      }
    }
  }, []);

  // Load Google
  useEffect(() => {
    const loadScript = (src, check) => new Promise(res => {
      if (check()) { res(); return; }
      const s = document.createElement("script"); s.src = src; s.onload = res; s.onerror = res; document.head.appendChild(s);
    });
    async function init() {
      try {
        await loadScript("https://apis.google.com/js/api.js", () => !!window.gapi);
        await new Promise((res, rej) => window.gapi.load("client", { callback: res, onerror: rej }));
        await window.gapi.client.init({
          apiKey: process.env.REACT_APP_GOOGLE_API_KEY, discoveryDocs: [
            "https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest",
          ]
        });
        setGapiReady(true);
      } catch (e) { console.error("GAPI:", e); }
      try {
        await loadScript("https://accounts.google.com/gsi/client", () => !!window.google?.accounts);
        const tc = window.google.accounts.oauth2.initTokenClient({
          client_id: CLIENT_ID, scope: SCOPES,
          callback: resp => { if (!resp.error && resp.access_token) { try { window.gapi.client.setToken({ access_token: resp.access_token }); } catch (e) { } setSignedIn(true); setLastSignedInState(true); LSset("flofax_lastSignedIn", true); setOfflineMode(false); } },
        });
        setTokenClient(tc); setGisReady(true);
      } catch (e) { console.error("GIS:", e); }
    }
    init();
  }, []);

  const handleSignIn = useCallback(() => {
    if (typeof window !== "undefined" && !navigator.onLine) {
      const cached = LS("flofax_cachedEvents", []);
      if (cached.length && LS("flofax_lastSignedIn", false)) {
        setEvents(cached); setSignedIn(true); setOfflineMode(true); setLastSignedInState(true); LSset("flofax_lastSignedIn", true);
        return;
      }
      alert("Offline mode: no cached data available yet. Connect to the internet to sign in first.");
      return;
    }
    tokenClient?.requestAccessToken({ prompt: "consent" });
  }, [tokenClient]);
  const handleSignOut = useCallback(() => {
    const t = window.gapi?.client?.getToken();
    if (t) window.google.accounts.oauth2.revoke(t.access_token, () => { });
    window.gapi?.client?.setToken(null);
    setSignedIn(false); setEvents([]); setLastSignedInState(false); LSset("flofax_lastSignedIn", false);
  }, []);

  const goToToday = useCallback(() => {
    setCurrentWeekStart(startOfWeek(new Date()));
    const dow = new Date().getDay();
    setPageIndex(dow === 0 || dow >= 4 ? 1 : 0);
  }, []);

  useEffect(() => { if (signedIn) fetchEvents(); }, [signedIn, currentWeekStart]);

  // ─── Service worker registration ─────────────────────────────────────────────
  const swRegRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    navigator.serviceWorker.register("/sw.js")
      .then(reg => {
        swRegRef.current = reg;
        console.log("[FloFax] SW registered:", reg.scope);
      })
      .catch(err => console.warn("[FloFax] SW registration failed:", err));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    setNotificationPermission(Notification.permission);
  }, []);

  // No-op: SW alarm approach doesn't use JS timers (kept for cleanup safety)
  function clearNotificationTimers() {
    notificationTimers.current.forEach(clearTimeout);
    notificationTimers.current = [];
  }

  // Post a notification immediately via the SW (works on mobile/backgrounded)
  async function postNotification(title, body) {
    if (typeof window === "undefined" || notificationPermission !== "granted") return;
    try {
      // Prefer SW showNotification — works on Android Chrome & installed PWAs
      if ("serviceWorker" in navigator) {
        const reg = swRegRef.current || await navigator.serviceWorker.ready;
        await reg.showNotification(title, {
          body,
          icon: "/flofax-icon-192.png",
          badge: "/flofax-icon-192.png",
          tag: "flofax-summary",
          requireInteraction: false,
          vibrate: [200, 100, 200],
        });
        return;
      }
    } catch (e) {
      console.warn("[FloFax] SW showNotification failed, falling back:", e);
    }
    // Fallback: plain Notification API (desktop browsers only)
    try { new Notification(title, { body }); } catch (e) { console.error(e); }
  }

  // Build summary from the localStorage cache so it works even from the SW alarm callback
  function buildSummaryForDate(date, label) {
    const allEvents = LS("flofax_cachedEvents", events);
    const dayEvents = allEvents.filter(ev => {
      const start = ev.start?.dateTime || ev.start?.date;
      return start && isSameDay(new Date(start), date);
    });
    if (dayEvents.length === 0) {
      return `No ${label} activities are scheduled. Enjoy a calm ${label}.`;
    }
    const lines = dayEvents.slice(0, 4).map(ev => {
      const { clean, time } = parseEventText(ev.summary);
      const prefix = time ? `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")} ` : "";
      return `${prefix}${clean || ev.summary}`;
    });
    return `${label === "today" ? "Here's your day:" : "Here's tomorrow:"}\n• ${lines.join("\n• ")}${dayEvents.length > 4 ? `\n…and ${dayEvents.length - 4} more.` : ""}`;
  }

  // Send alarm schedule to the SW so notifications fire even when tab is backgrounded
  function scheduleNotifications() {
    clearNotificationTimers();
    if (notificationPermission !== "granted") return;

    const now = new Date();
    const makeTarget = (timeString, dateRef) => {
      const [hour, minute] = timeString.split(":").map(n => Number(n));
      const target = new Date(dateRef);
      target.setHours(hour, minute, 0, 0);
      if (target.getTime() <= dateRef.getTime()) target.setDate(target.getDate() + 1);
      return target;
    };

    const alarms = [];

    if (morningNotificationEnabled) {
      const target = makeTarget(morningNotificationTime, now);
      alarms.push({
        id: "morning",
        fireAt: target.toISOString(),
        title: "Good morning from FloFax 🌅",
        body: buildSummaryForDate(target, "today"),
      });
      // Also keep a JS timer as a fallback for when the app tab is open
      notificationTimers.current.push(setTimeout(() => {
        postNotification("Good morning from FloFax 🌅", buildSummaryForDate(new Date(), "today"));
        scheduleNotifications();
      }, target.getTime() - now.getTime()));
    }

    if (bedtimeNotificationEnabled) {
      const target = makeTarget(bedtimeNotificationTime, now);
      alarms.push({
        id: "bedtime",
        fireAt: target.toISOString(),
        title: "FloFax — Tomorrow's plan 🌙",
        body: buildSummaryForDate(addDays(target, 1), "next day"),
      });
      // JS timer fallback
      notificationTimers.current.push(setTimeout(() => {
        postNotification("FloFax — Tomorrow's plan 🌙", buildSummaryForDate(addDays(new Date(), 1), "next day"));
        scheduleNotifications();
      }, target.getTime() - now.getTime()));
    }

    // Send alarms to the SW — this is what fires even when the page is backgrounded
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(reg => {
        reg.active?.postMessage({ type: alarms.length ? "SET_ALARMS" : "CLEAR_ALARMS", payload: alarms });
      }).catch(() => {});
    }
  }

  async function requestNotificationPermission() {
    if (typeof window === "undefined" || !("Notification" in window)) {
      alert("Notifications are not supported by this browser.");
      return;
    }
    // On iOS Safari 16.4+ we need to request inside a user gesture AND the app must be
    // installed as a PWA (Add to Home Screen) for SW notifications to work.
    const result = await Notification.requestPermission();
    setNotificationPermission(result);
    if (result === "granted") {
      // Send a test notification via SW to confirm it works
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.ready.then(reg => {
          reg.active?.postMessage({
            type: "TEST_NOTIFICATION",
            payload: { title: "FloFax notifications enabled ✅", body: "Morning and bedtime summaries are now active." }
          });
        }).catch(() => {
          // Fallback to plain Notification
          try { new Notification("FloFax notifications enabled ✅", { body: "Morning and bedtime summaries are now active." }); } catch (_) {}
        });
      } else {
        try { new Notification("FloFax notifications enabled ✅", { body: "Morning and bedtime summaries are now active." }); } catch (_) {}
      }
      scheduleNotifications();
    } else {
      alert("Please allow notifications in your browser settings to receive summaries.\n\nOn iPhone: add FloFax to your Home Screen first, then enable notifications.");
    }
  }

  function compareVersions(remote, local) {
    const a = String(remote).split('.').map(Number);
    const b = String(local).split('.').map(Number);
    for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
      const ra = a[i] || 0;
      const rb = b[i] || 0;
      if (ra > rb) return 1;
      if (ra < rb) return -1;
    }
    return 0;
  }

  async function checkForAppUpdate() {
    if (typeof window === "undefined") return;
    try {
      setCheckingUpdate(true);
      const response = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" });
      if (!response.ok) return;
      const data = await response.json();
      if (data.version && compareVersions(data.version, APP_VERSION) > 0) {
        setRemoteVersion(data.version);
        setUpdateAvailable(true);
        setShowUpdatePrompt(true);
      }
    } catch (e) {
      console.error("Update check failed:", e);
    } finally {
      setCheckingUpdate(false);
    }
  }

  useEffect(() => {
    checkForAppUpdate();
    const interval = setInterval(checkForAppUpdate, UPDATE_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  function applyAppUpdate() {
    if (navigator?.serviceWorker?.getRegistrations) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        regs.forEach(reg => reg.update());
      }).catch(() => {});
    }
    window.location.reload();
  }

  useEffect(() => {
    scheduleNotifications();
    return clearNotificationTimers;
  }, [events, morningNotificationEnabled, morningNotificationTime, bedtimeNotificationEnabled, bedtimeNotificationTime, notificationPermission]);

  async function fetchEvents() {
    setLoading(true);
    try {
      // Fetch events from all calendars the user has access to (includes shared calendars)
      const calListRes = await window.gapi.client.calendar.calendarList.list();
      const cals = (calListRes.result.items || []).map(c => c.id);
      const all = [];
      await Promise.all(cals.map(async calId => {
        try {
          const res = await window.gapi.client.calendar.events.list({
            calendarId: calId, singleEvents: true, orderBy: "startTime", maxResults: 250,
            timeMin: new Date(currentWeekStart).toISOString(),
            timeMax: addDays(currentWeekStart, 7).toISOString(),
          });
          (res.result.items || []).forEach(it => { it._calendarId = calId; all.push(it); });
        } catch (e) { /* ignore per-calendar failures */ }
      }));
      all.sort((a, b) => new Date((a.start?.dateTime || a.start?.date)).getTime() - new Date((b.start?.dateTime || b.start?.date)).getTime());
      setEvents(all);
      LSset("flofax_cachedEvents", all);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function getEventsForDay(dayDate) {
    return events.filter(ev => {
      const s = ev.start?.dateTime || ev.start?.date;
      return s && isSameDay(new Date(s), dayDate);
    });
  }

  // Navigation
  function onTouchStart(e) { touchStartX.current = e.touches[0].clientX; touchStartY.current = e.touches[0].clientY; }
  function onTouchEnd(e) {
    if (touchStartX.current === null || animating) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) < Math.abs(dy) || Math.abs(dx) < 40) return;
    if (dx < 0) navigate(1, isLandscapeRef.current);
    else navigate(-1, isLandscapeRef.current);
    touchStartX.current = null;
  }

  function navigate(dir, landscape) {
    if (animating) return;
    setSwipeDir(dir > 0 ? "left" : "right"); setAnimating(true);
    setTimeout(() => {
      if (landscape) {
        setCurrentWeekStart(w => addDays(w, dir * 7));
      } else {
        if (dir > 0) {
          if (pageIndex === 0) setPageIndex(1);
          else { setCurrentWeekStart(w => addDays(w, 7)); setPageIndex(0); }
        } else {
          if (pageIndex === 1) setPageIndex(0);
          else { setCurrentWeekStart(w => addDays(w, -7)); setPageIndex(1); }
        }
      }
      setSwipeDir(null); setAnimating(false);
    }, 320);
  }

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const weekRange = `${formatShort(weekDays[0])} – ${formatShort(weekDays[6])}`;
  const leftDays = weekDays.slice(0, 3);
  const rightDays = weekDays.slice(3);
  const pageLabel = pageIndex === 0
    ? `${formatShort(weekDays[0])} – ${formatShort(weekDays[2])}`
    : `${formatShort(weekDays[3])} – ${formatShort(weekDays[6])}`;

  // Inject theme CSS vars and font
  const themeCSS = `
    :root {
      --leather-dark:${theme.leatherDark};--leather-mid:${theme.leatherMid};
      --leather-light:${theme.leatherLight};--leather-tan:${theme.leatherTan};
      --paper-cream:${paper.bg};--paper-warm:${paper.warm};--paper-aged:${paper.aged};
      --ring-gold:${theme.ringGold};
      --event-font:${font.family};
    }`;

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=${font.import}&display=swap');`}</style>
      <style>{themeCSS}</style>
      <style>{BASE_CSS}</style>
      <div className="app-shell">
        {!signedIn ? (
          <SignInScreen
            onSignIn={handleSignIn}
            gapiReady={gapiReady}
            gisReady={gisReady}
            version={APP_VERSION}
            offlineMode={offlineMode}
            cachedEvents={hasCachedEvents}
          />
        ) : (
          <div className="planner-container" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {/* Top bar */}
            <div className="top-bar">
              <div>
                <div className="top-bar-title">FloFax</div>
                <div className="top-bar-week">{weekRange}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <select className="period-select" onChange={e => {
                  const v = e.target.value; if (!v) return;
                  const now = new Date(); let start = null, end = null;
                  if (v.startsWith('last')) { const n = Number(v.split(' ')[1]); end = new Date(); start = addDays(end, -30 * n); }
                  else if (v.startsWith('next')) { const n = Number(v.split(' ')[1]); start = new Date(); end = addDays(start, 30 * n); }
                  const reverse = v.startsWith('last');
                  setEventsListRange({ start, end }); setShowEventsList(true);
                  fetchEventsInRange(start, end).then(evts => setEventsList(reverse ? evts.reverse() : evts));
                }} defaultValue="">
                  <option value="" disabled>Show…</option>
                  <option value="last 6">-6mths</option>
                  <option value="last 3">-3mths</option>
                  <option value="last 1">-1mth</option>
                  <option value="next 1">+1mth</option>
                  <option value="next 3">+3mths</option>
                  <option value="next 6">+6mths</option>
                </select>
                <button className="icon-btn" title="Tasks" onClick={() => setShowTasks(true)}>🗒</button>
              </div>
              <div className="top-bar-actions">
                <button className="icon-btn" onClick={goToToday} title="Today">◉</button>
                <button className="icon-btn" onClick={() => setShowSettings(true)} title="Settings" style={{ fontSize: 15 }}>⚙️</button>
                <button className="icon-btn" onClick={handleSignOut} title="Sign out" style={{ fontSize: 13 }}>⏏</button>
              </div>
            </div>
            {updateAvailable && showUpdatePrompt && (
              <div className="update-banner">
                <div>
                  A newer version of FloFax ({remoteVersion}) is available.
                  Tap update to refresh to the latest version.
                </div>
                <div className="update-actions">
                  <button className="update-later" onClick={() => setShowUpdatePrompt(false)}>Later</button>
                  <button className="update-now" onClick={applyAppUpdate}>Update now</button>
                </div>
              </div>
            )}


            {/* Pages */}
            <div className="page-viewport">
              {isLandscape ? (
                <div className={`page-wrapper landscape${swipeDir === " left" ? " slide-exit-left" : swipeDir === "right" ? " slide-exit-right" : ""}`}>
                  <FloFaxPage pageIndex={0} days={leftDays} weekDays={weekDays} weekEvents={events}
                    getEventsForDay={getEventsForDay} onAddEntry={d => setAddModal({ day: d })}
                    onEditEvent={ev => setEditModal({ event: ev })}
                    today={new Date()} colorRules={colorRules} tabLabel="MON–WED"
                    isLandscape showSummary font={font} onShowWeekDetails={evts => { setWeekDetailsEvents(evts); setShowWeekDetails(true); }} />
                  <div className="book-spine" />
                  <FloFaxPage pageIndex={1} days={rightDays} weekDays={weekDays} weekEvents={events}
                    getEventsForDay={getEventsForDay} onAddEntry={d => setAddModal({ day: d })}
                    onEditEvent={ev => setEditModal({ event: ev })}
                    today={new Date()} colorRules={colorRules} tabLabel="THU–SUN"
                    isLandscape showSummary={false} font={font} onShowWeekDetails={evts => { setWeekDetailsEvents(evts); setShowWeekDetails(true); }} />
                </div>
              ) : (
                <div className={`page-wrapper${swipeDir === "left" ? " slide-exit-left" : swipeDir === "right" ? " slide-exit-right" : ""}`}>
                  <FloFaxPage
                    pageIndex={pageIndex}
                    days={pageIndex === 0 ? leftDays : rightDays}
                    weekDays={weekDays} weekEvents={events}
                    getEventsForDay={getEventsForDay} onAddEntry={d => setAddModal({ day: d })}
                    onEditEvent={ev => setEditModal({ event: ev })}
                    today={new Date()} colorRules={colorRules}
                    tabLabel={pageIndex === 0 ? "MON–WED" : "THU–SUN"}
                    isLandscape={false} showSummary={pageIndex === 0} font={font} onShowWeekDetails={evts => { setWeekDetailsEvents(evts); setShowWeekDetails(true); }} />
                </div>
              )}
              {loading && <div className="loading-overlay"><span className="saving-spinner" />Fetching calendar…</div>}
            </div>

            {/* Nav */}
            {!isLandscape ? (
              <div className="nav-strip">
                <button className="nav-btn" onClick={() => navigate(-1, false)}>‹</button>
                <div className="nav-center">
                  <div className="nav-week-label">{pageLabel}</div>
                  <div className="nav-page-dots">
                    <div className={`nav-dot${pageIndex === 0 ? " active" : ""}`} />
                    <div className={`nav-dot${pageIndex === 1 ? " active" : ""}`} />
                  </div>
                </div>
                <button className="nav-add-btn" onClick={() => setAddModal({ day: null })}>+ Add Entry</button>
                <button className="nav-btn" onClick={() => navigate(1, false)}>›</button>
              </div>
            ) : (
              <div className="nav-strip landscape-nav">
                <button className="nav-btn" onClick={() => navigate(-1, true)}>‹ Prev Week</button>
                <div className="nav-center"><div className="nav-week-label">{weekRange}</div></div>
                <button className="nav-add-btn" onClick={() => setAddModal({ day: null })}>+ Add Entry</button>
                <button className="nav-btn" onClick={() => navigate(1, true)}>Next Week ›</button>
              </div>
            )}
          </div>
        )}

        {addModal && (
          <EventModal mode="add" day={addModal.day} event={null}
            onClose={() => setAddModal(null)} onSaved={fetchEvents} />
        )}
        {editModal && (
          <EventModal mode="edit" day={null} event={editModal.event}
            onClose={() => setEditModal(null)} onSaved={fetchEvents} />
        )}
        {showSettings && (
          <SettingsPanel
            colorRules={colorRules} onColorRules={setColorRules}
            themeId={themeId} onTheme={setThemeId}
            paperId={paperId} onPaper={setPaperId}
            fontId={fontId} onFont={setFontId}
            morningNotificationEnabled={morningNotificationEnabled}
            onMorningNotificationEnabled={setMorningNotificationEnabled}
            morningNotificationTime={morningNotificationTime}
            onMorningNotificationTime={setMorningNotificationTime}
            bedtimeNotificationEnabled={bedtimeNotificationEnabled}
            onBedtimeNotificationEnabled={setBedtimeNotificationEnabled}
            bedtimeNotificationTime={bedtimeNotificationTime}
            onBedtimeNotificationTime={setBedtimeNotificationTime}
            notificationPermission={notificationPermission}
            onRequestNotificationPermission={requestNotificationPermission}
            onClose={() => setShowSettings(false)} />
        )}
        {showEventsList && (
          <EventsListModal start={eventsListRange?.start} end={eventsListRange?.end} events={eventsList} onClose={() => setShowEventsList(false)} />
        )}
        {showTasks && (
          <TasksPanel onClose={() => setShowTasks(false)} tokenClient={tokenClient} />
        )}
        {showWeekDetails && (
          <WeekEventsModal events={weekDetailsEvents} colorRules={colorRules} onClose={() => setShowWeekDetails(false)} />
        )}
      </div>
    </>
  );
}

// ─── Sign-In ──────────────────────────────────────────────────────────────────
function SignInScreen({ onSignIn, gapiReady, gisReady, version, offlineMode, cachedEvents }) {
  const ready = gapiReady && gisReady;
  return (
    <div className="signin-screen">
      <div className="signin-logo">𝔽</div>
      <div>
        <div className="signin-title">FloFax Planner</div>
        <div className="signin-sub" style={{ marginTop: 6 }}>Your personal calendar,<br />beautifully bound.</div>
      </div>
      <button className="signin-btn" onClick={onSignIn} disabled={!ready && !cachedEvents}>
        {!ready ? (
          cachedEvents ? "Use offline cache" : <><span className="saving-spinner" />Loading…</>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" /></svg>
            Sign in with Google
          </>
        )}
      </button>
      <div className="signin-sub" style={{ fontSize: 11, opacity: 0.6 }}>
        {offlineMode ? (cachedEvents ? "Offline cache available — tap to view cached events." : "Offline and no cache yet.") : "Requires Google Calendar access"}
      </div>
      <div className="signin-sub" style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>
        App version {version}
      </div>
      <div style={{ fontSize: 11, marginTop: 16 }}>
        <a href="https://flofax.autom8sustain.com/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: "var(--leather-tan)", opacity: 0.6, textDecoration: "underline", margin: "0 8px", pointerEvents: "auto" }}>Privacy Policy</a>
        <span style={{ color: "var(--leather-tan)", opacity: 0.4 }}>•</span>
        <a href="https://flofax.autom8sustain.com/terms-of-service.html" target="_blank" rel="noopener noreferrer" style={{ color: "var(--leather-tan)", opacity: 0.6, textDecoration: "underline", margin: "0 8px", pointerEvents: "auto" }}>Terms of Service</a>
      </div>
    </div>
  );
}

// ─── FloFax Page ─────────────────────────────────────────────────────────────
function FloFaxPage({ days, weekDays, weekEvents, getEventsForDay, onAddEntry, onEditEvent, today, colorRules, tabLabel, isLandscape, showSummary, font, onShowWeekDetails, pageIndex }) {
  const slots = (showSummary ? 1 : 0) + (days?.length || 0);
  const gridClass = slots === 4 ? " four-grid" : "";
  const isLeftPage = isLandscape && pageIndex === 0;
  return (
    <div className={`flofax-page${isLandscape ? " landscape-page" : ""}${isLeftPage ? " left-page" : ""}${gridClass}`}>
      <div className="binding-holes">{Array.from({ length: 7 }).map((_, i) => <div key={i} className="hole" />)}</div>
      <div className="page-tab">{tabLabel}</div>
      {!isLandscape && <div className="turn-hint"><span className="turn-hint-arrow">›</span></div>}
      {/* Ruled lines — rendered as a separate scrolling layer INSIDE the scroll container */}
      <div className="page-scroll-container">
        <div className="ruled-lines-overlay" />
        <div className="margin-line-overlay" />
        <div className="page-inner-content" style={{ fontFamily: font?.family }}>
          {showSummary && <WeekSummary weekDays={weekDays} weekEvents={weekEvents} colorRules={colorRules} onShowDetails={onShowWeekDetails} />}
          {days.map((day, i) => (
            <DaySection key={i} day={day} events={getEventsForDay(day)}
              onAdd={() => onAddEntry(day)} onEdit={onEditEvent}
              today={today} colorRules={colorRules} isLast={i === days.length - 1} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Week Summary ─────────────────────────────────────────────────────────────
function WeekSummary({ weekEvents, colorRules, onShowDetails }) {
  const total = weekEvents.length;
  return (
    <div className="week-summary">
      <div className="section-label">Week Overview</div>
      <div className="week-summary-title">{total === 0 ? "A clear week ahead" : `${total} ${total === 1 ? "event" : "events"} this week`}</div>
      {total > 0 && (
        <div className="week-summary-pills">
          {weekEvents.slice(0, 5).map((ev, i) => (
            <div key={i} className="week-pill">
              <div className="week-pill-dot" style={{ background: getEventColor(ev.summary, colorRules) }} />
              <span>{ev.summary || "No title"}{getEventIcon(ev.summary) && <span style={{ marginLeft: 3 }}>{getEventIcon(ev.summary)}</span>}</span>
            </div>
          ))}
          {total > 5 && (
            <div className="week-pill week-pill-more" onClick={() => onShowDetails?.(weekEvents)}>
              +{total - 5} more
            </div>
          )}
        </div>
      )}
      {total === 0 && <div className="week-empty">No events scheduled · enjoy the calm</div>}
    </div>
  );
}

function WeekEventsModal({ events, colorRules, onClose }) {
  function formatDateLong(iso) { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet" style={{ maxWidth: 520, maxHeight: '80vh', overflow: 'hidden' }}>
        <div className="modal-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div className="modal-title">All week events</div>
              <div className="modal-sub">{events.length} {events.length === 1 ? 'event' : 'events'} this week</div>
            </div>
            <button className="modal-close-btn" onClick={onClose}>✕</button>
          </div>
          <div style={{ maxHeight: '64vh', overflowY: 'auto' }}>
            {events.map((ev, i) => {
              const timeStr = ev.start?.dateTime ? formatTime(ev.start.dateTime) : (ev.start?.date ? 'All day' : '');
              const color = getEventColor(ev.summary, colorRules);
              return (
                <div key={i} style={{ padding: 10, borderBottom: '1px solid rgba(60,40,20,0.08)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 1 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-dark)' }}>
                      {ev.summary || 'No title'}{getEventIcon(ev.summary) && <span style={{ marginLeft: 6 }}>{getEventIcon(ev.summary)}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-mid)' }}>{timeStr}{ev.start?.dateTime ? ` · ${formatDateLong(ev.start.dateTime)}` : ev.start?.date ? ` · ${formatDateLong(ev.start.date)}` : ''}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Day Section ──────────────────────────────────────────────────────────────
function DaySection({ day, events, onAdd, onEdit, today, colorRules, isLast }) {
  const isToday = isSameDay(day, today);
  const name = DAY_NAMES[(day.getDay() + 6) % 7];
  return (
    <div className={`day-section${isLast ? " last" : ""}`}>
      <div className="day-header">
        <span className={`day-name${isToday ? " today" : ""}`}>{name}</span>
        <span className="day-num">{formatShort(day)}</span>
        {isToday && <span className="day-today-badge">Today</span>}
        <button className="day-add-btn" onClick={e => { e.stopPropagation(); onAdd(day); }}>+</button>
      </div>
      <div className="day-events">
        {events.length === 0 ? (
          <><div className="day-empty-line" /><div className="day-empty-line" /></>
        ) : (
          events.slice(0, 5).map((ev, i) => {
            const isAllDay = !!ev.start?.date && !ev.start?.dateTime;
            const timeStr = isAllDay ? "All day" : formatTime(ev.start?.dateTime);
            const color = getEventColor(ev.summary, colorRules);
            const icon = getEventIcon(ev.summary);
            const { clean, phone, email, postcode, time } = parseEventText(ev.summary);
            const isAndroid = /Android/i.test(navigator.userAgent);
            const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
            return (
              <div key={i} className="event-entry" onClick={() => onEdit(ev)} title="Tap to edit">
                <div className="event-bullet" style={{ background: color }} />
                <span className="event-time">{timeStr}</span>
                <span className="event-title">
                  {clean}
                  {icon && <span className="event-icon">{icon}</span>}
                  {postcode && <a className="event-action-icon" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(postcode)}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title={postcode}>📍</a>}
                  {phone && <a className="event-action-icon" href={`tel:${phone.replace(/\s/g, "")}`} onClick={e => e.stopPropagation()} title={phone}>📞</a>}
                  {email && <a className="event-action-icon" href={`mailto:${email}`} onClick={e => e.stopPropagation()} title={email}>✉️</a>}
                  {time && (
                    <a
                      className="event-action-icon"
                      href={isAndroid ? `intent://#Intent;action=android.intent.action.SET_ALARM;i.android.intent.extra.alarm.HOUR=${time.hour};i.android.intent.extra.alarm.MINUTES=${time.minute};S.android.intent.extra.alarm.MESSAGE=${encodeURIComponent(clean)};end` : (isIOS ? "clock-alarm://" : "#")}
                      onClick={e => {
                        e.stopPropagation();
                        if (!isAndroid && !isIOS) {
                          e.preventDefault();
                          alert(`Alarm setting helper:\nSet an alarm on your phone for ${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')} - "${clean}"`);
                        }
                      }}
                      title={`Set alarm for ${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`}
                      target={isAndroid ? "_self" : "_blank"}
                      rel="noreferrer"
                    >
                      ⏰
                    </a>
                  )}
                </span>
              </div>
            );
          })
        )}
        {events.length > 5 && <div style={{ fontSize: 10, color: "var(--ink-mid)", fontStyle: "italic", paddingLeft: 8 }}>+{events.length - 5} more</div>}
      </div>
    </div>
  );
}

// ─── Event Modal (Add & Edit) ─────────────────────────────────────────────────
function EventModal({ mode, day, event, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [inputMode, setInputMode] = useState("type");
  const [title, setTitle] = useState(isEdit ? (event.summary || "") : "");
  const [date, setDate] = useState(() => {
    if (isEdit) {
      const s = event.start?.dateTime || event.start?.date;
      return s ? s.split("T")[0] : toDateInput(new Date());
    }
    return day ? toDateInput(day) : toDateInput(new Date());
  });
  const [startTime, setStartTime] = useState(() => {
    if (isEdit && event.start?.dateTime) return formatTime(event.start.dateTime);
    return "09:00";
  });
  const [endTime, setEndTime] = useState(() => {
    if (isEdit && event.end?.dateTime) return formatTime(event.end.dateTime);
    return "10:00";
  });
  const [allDay, setAllDay] = useState(isEdit ? (!!event.start?.date && !event.start?.dateTime) : false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const recRef = useRef(null);
  const transcriptRef = useRef("");

  useEffect(() => {
    if (!title.trim() && transcript.trim()) {
      setTitle(transcript.trim());
    }
  }, [transcript, title]);

  const dayName = day ? DAY_NAMES[(day.getDay() + 6) % 7] : isEdit ? DAY_NAMES[(new Date(event.start?.dateTime || event.start?.date).getDay() + 6) % 7] : "";

  async function parseVoice(text) {
    setVoiceProcessing(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514", max_tokens: 300,
          messages: [{ role: "user", content: `Today is ${new Date().toISOString().split("T")[0]}. Parse this calendar entry, return ONLY JSON: {title,date(YYYY-MM-DD),startTime(HH:MM),endTime(HH:MM),allDay(bool)}. No date→today, no time→09:00-10:00.\nEntry: "${text}"` }]
        })
      });
      const data = await res.json();
      const parsed = JSON.parse(data.content.map(b => b.text || "").join("").replace(/```json|```/g, "").trim());
      if (parsed.title) setTitle(parsed.title);
      if (parsed.date) setDate(parsed.date);
      if (parsed.startTime) setStartTime(parsed.startTime);
      if (parsed.endTime) setEndTime(parsed.endTime);
      if (parsed.allDay !== undefined) setAllDay(parsed.allDay);
      setInputMode("type");
    } catch (e) {
      console.error(e);
      const parsed = localParseVoice(text);
      if (parsed.title) setTitle(parsed.title);
      if (parsed.date) setDate(parsed.date);
      if (parsed.startTime) setStartTime(parsed.startTime);
      if (parsed.endTime) setEndTime(parsed.endTime);
      if (parsed.allDay !== undefined) setAllDay(parsed.allDay);
      setInputMode("type");
    }
    setVoiceProcessing(false);
  }

  function startListen() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Speech not supported. Type your entry."); return; }
    const rec = new SR(); rec.lang = "en-GB"; rec.continuous = false; rec.interimResults = true;
    rec.onresult = e => { const t = Array.from(e.results).map(r => r[0].transcript).join(" "); setTranscript(t); transcriptRef.current = t; };
    rec.onend = () => { setListening(false); if (transcriptRef.current) parseVoice(transcriptRef.current); };
    rec.start(); recRef.current = rec; setListening(true); setTranscript(""); transcriptRef.current = "";
  }
  function stopListen() { recRef.current?.stop(); setListening(false); }

  async function handleSave() {
    const finalTitle = title.trim() || transcript.trim();
    if (!finalTitle) return;
    setSaving(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const body = {
        summary: finalTitle,
        start: allDay ? { date } : { dateTime: `${date}T${startTime}:00`, timeZone: tz },
        end: allDay ? { date } : { dateTime: `${date}T${endTime}:00`, timeZone: tz },
      };
      if (isEdit) {
        await window.gapi.client.calendar.events.update({ calendarId: "primary", eventId: event.id, resource: body });
      } else {
        await window.gapi.client.calendar.events.insert({ calendarId: "primary", resource: body });
      }
      onSaved(); onClose();
    } catch (e) { console.error(e); alert("Failed to save. Check Google Calendar access."); }
    setSaving(false);
  }

  async function handleDelete() {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    try {
      await window.gapi.client.calendar.events.delete({ calendarId: "primary", eventId: event.id });
      onSaved(); onClose();
    } catch (e) { console.error(e); alert("Failed to delete event."); }
    setDeleting(false);
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet">
        <div className="modal-content">
          <div className="modal-handle" />
          <div className="modal-title">{isEdit ? "Edit Entry" : "New Entry"}</div>
          <div className="modal-sub">{dayName}{date ? ` · ${formatDate(new Date(date + "T12:00:00"))}` : ""}</div>

          <div className="modal-tabs">
            <button className={`modal-tab${inputMode === "type" ? " active" : ""}`} onClick={() => setInputMode("type")}>✏️ Type</button>
            <button className={`modal-tab${inputMode === "voice" ? " active" : ""}`} onClick={() => setInputMode("voice")}>🎙 Voice</button>
          </div>

          {inputMode === "type" ? (<>
            <div className="modal-field">
              <div className="modal-label">Event Title</div>
              <input className="modal-input" value={title} onChange={e => setTitle(e.target.value)} autoFocus placeholder="e.g. Board meeting, Lunch with Sarah…" />
            </div>
            <div className="modal-field">
              <div className="modal-label">Date</div>
              <input type="date" className="modal-input" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink-mid)", cursor: "pointer" }}>
                <input type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} /> All day event
              </label>
            </div>
            {!allDay && (
              <div className="modal-row">
                <div className="modal-field"><div className="modal-label">Start</div><input type="time" className="modal-input" value={startTime} onChange={e => setStartTime(e.target.value)} /></div>
                <div className="modal-field"><div className="modal-label">End</div><input type="time" className="modal-input" value={endTime} onChange={e => setEndTime(e.target.value)} /></div>
              </div>
            )}
          </>) : (
            <div className="voice-area">
              <button className={`voice-btn${listening ? " listening" : ""}`} onClick={listening ? stopListen : startListen} disabled={voiceProcessing}>
                {voiceProcessing ? <span className="saving-spinner" style={{ borderTopColor: "white" }} /> : (listening ? "⏹" : "🎙")}
              </button>
              <div className="voice-status">{voiceProcessing ? "Parsing your entry…" : listening ? "Listening… tap to stop" : `Tap mic and speak\ne.g. "Team lunch tomorrow at 1pm"`}</div>
              {transcript && <div className="voice-transcript">"{transcript}"</div>}
            </div>
          )}

          <div className="modal-actions">
            {isEdit && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ flex: 1, padding: 12, background: confirmDelete ? "#8B1A1A" : "transparent", border: `1px solid ${confirmDelete ? "#8B1A1A" : "rgba(139,26,26,0.4)"}`, borderRadius: 3, cursor: "pointer", fontFamily: "'EB Garamond',serif", fontSize: 15, color: confirmDelete ? "white" : "#8B1A1A", transition: "all 0.2s" }}>
                {deleting ? <><span className="saving-spinner" style={{ borderTopColor: confirmDelete ? "white" : "#8B1A1A" }} />Deleting…</> : confirmDelete ? "Confirm Delete" : "🗑 Delete"}
              </button>
            )}
            <button className="modal-cancel" onClick={onClose}>Cancel</button>
            <button className="modal-save" onClick={handleSave} disabled={saving || (!title.trim() && !transcript.trim())}>
              {saving ? <><span className="saving-spinner" />Saving…</> : isEdit ? "Update Event" : "Save to Calendar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Panel ───────────────────────────────────────────────────────────
function SettingsPanel({
  colorRules, onColorRules, themeId, onTheme, paperId, onPaper, fontId, onFont,
  morningNotificationEnabled, onMorningNotificationEnabled,
  morningNotificationTime, onMorningNotificationTime,
  bedtimeNotificationEnabled, onBedtimeNotificationEnabled,
  bedtimeNotificationTime, onBedtimeNotificationTime,
  notificationPermission, onRequestNotificationPermission,
  onClose
}) {
  const [tab, setTab] = useState("appearance");
  const [draftRules, setDraftRules] = useState(colorRules);
  const [newKw, setNewKw] = useState("");
  const [newCol, setNewCol] = useState("#2C5F8A");

  function updateRule(i, field, val) { const r = [...draftRules]; r[i] = { ...r[i], [field]: val }; setDraftRules(r); }
  function removeRule(i) { setDraftRules(draftRules.filter((_, idx) => idx !== i)); }
  function addRule() { if (!newKw.trim()) return; setDraftRules([...draftRules, { keyword: newKw.trim().toLowerCase(), color: newCol }]); setNewKw(""); setNewCol("#2C5F8A"); }
  function saveAndClose() { onColorRules(draftRules); onClose(); }

  const sectionTitle = { fontFamily: "'Playfair Display',serif", fontSize: 12, fontWeight: 700, color: "var(--accent-red)", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10, marginTop: 16 };
  const pill = (active, onClick, label) => (
    <button onClick={onClick} style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${active ? "var(--ring-gold)" : "rgba(60,40,20,0.2)"}`, background: active ? "var(--leather-dark)" : "transparent", color: active ? "var(--ring-gold)" : "var(--ink-mid)", cursor: "pointer", fontFamily: "'EB Garamond',serif", fontSize: 13, transition: "all 0.15s", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) saveAndClose(); }}>
      <div className="modal-sheet" style={{ maxHeight: "88vh", overflowY: "auto" }}>
        <div className="modal-content">
          <div className="modal-handle" />
          <div className="modal-title">Settings</div>

          {/* Tab row */}
          <div className="modal-tabs" style={{ marginBottom: 12 }}>
            <button className={`modal-tab${tab === "appearance" ? " active" : ""}`} onClick={() => setTab("appearance")}>🎨 Appearance</button>
            <button className={`modal-tab${tab === "colours" ? " active" : ""}`} onClick={() => setTab("colours")}>🏷 Colours</button>
            <button className={`modal-tab${tab === "notifications" ? " active" : ""}`} onClick={() => setTab("notifications")}>🔔 Notifications</button>
          </div>

          {tab === "appearance" && (<>
            <div style={sectionTitle}>Leather Colour</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
              {THEMES.map(t => (
                <div key={t.id} onClick={() => onTheme(t.id)}
                  style={{
                    width: 44, height: 44, borderRadius: 8, cursor: "pointer",
                    background: `linear-gradient(135deg,${t.leatherMid},${t.leatherDark})`,
                    border: `2.5px solid ${t.id === themeId ? t.ringGold : "transparent"}`,
                    boxShadow: t.id === themeId ? `0 0 0 2px ${t.ringGold}40` : "none",
                    position: "relative", transition: "all 0.15s", display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 3
                  }}>
                  <span style={{ fontSize: 8, color: t.ringGold, fontFamily: "'Playfair Display',serif", letterSpacing: "0.05em", opacity: t.id === themeId ? 1 : 0.7 }}>
                    {t.name.split(" ")[0]}
                  </span>
                </div>
              ))}
            </div>

            <div style={sectionTitle}>Page Colour</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
              {PAPER_STYLES.map(p => (
                <div key={p.id} onClick={() => onPaper(p.id)}
                  style={{
                    flex: 1, height: 40, borderRadius: 6, cursor: "pointer",
                    background: p.bg,
                    border: `2.5px solid ${p.id === paperId ? "var(--ring-gold)" : "rgba(60,40,20,0.2)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "'EB Garamond',serif", fontSize: 13, color: "var(--ink-mid)", transition: "all 0.15s"
                  }}>
                  {p.name}
                </div>
              ))}
            </div>

            <div style={sectionTitle}>Event Font</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {FONTS.map(f => (
                <div key={f.id} onClick={() => onFont(f.id)}
                  style={{
                    padding: "10px 14px", borderRadius: 4, cursor: "pointer",
                    border: `1.5px solid ${f.id === fontId ? "var(--ring-gold)" : "rgba(60,40,20,0.2)"}`,
                    background: f.id === fontId ? "rgba(200,168,75,0.08)" : "transparent",
                    display: "flex", justifyContent: "space-between", alignItems: "center", transition: "all 0.15s"
                  }}>
                  <span style={{ fontFamily: f.family, fontSize: 16, color: "var(--ink-dark)" }}>{f.name}</span>
                  <span style={{ fontFamily: f.family, fontSize: 13, color: "var(--ink-mid)", fontStyle: "italic" }}>Aa Bb Cc</span>
                </div>
              ))}
            </div>
          </>)}

          {tab === "colours" && (<>
            <div style={sectionTitle}>Keyword → Colour Rules</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 14 }}>
              {draftRules.map((rule, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input className="modal-input" style={{ flex: 1, padding: "6px 10px" }} value={rule.keyword} onChange={e => updateRule(i, "keyword", e.target.value)} />
                  <input type="color" value={rule.color} onChange={e => updateRule(i, "color", e.target.value)}
                    style={{ width: 36, height: 36, border: "1px solid rgba(60,40,20,0.2)", borderRadius: 3, cursor: "pointer", padding: 2 }} />
                  <button onClick={() => removeRule(i)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--accent-red)", lineHeight: 1, paddingBottom: 2 }}>×</button>
                </div>
              ))}
            </div>
            <div style={sectionTitle}>Add New Rule</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
              <input className="modal-input" style={{ flex: 1, padding: "8px 10px" }} placeholder="e.g. sarah, dentist, richard…"
                value={newKw} onChange={e => setNewKw(e.target.value)} onKeyDown={e => e.key === "Enter" && addRule()} />
              <input type="color" value={newCol} onChange={e => setNewCol(e.target.value)}
                style={{ width: 36, height: 36, border: "1px solid rgba(60,40,20,0.2)", borderRadius: 3, cursor: "pointer", padding: 2 }} />
              <button onClick={addRule} style={{ background: "var(--leather-dark)", border: "1px solid var(--ring-gold)", borderRadius: 3, color: "var(--ring-gold)", padding: "8px 14px", cursor: "pointer", fontFamily: "'EB Garamond',serif", fontSize: 15 }}>Add</button>
            </div>
          </>)}

          {tab === "notifications" && (<>
            <div style={sectionTitle}>Notifications</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--ink-dark)", cursor: "pointer" }}>
                <input type="checkbox" checked={morningNotificationEnabled} onChange={e => onMorningNotificationEnabled(e.target.checked)} />
                Morning summary
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 130, fontSize: 13, color: "var(--ink-mid)" }}>Send each morning at</span>
                <input type="time" className="modal-input" value={morningNotificationTime} onChange={e => onMorningNotificationTime(e.target.value)} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--ink-dark)", cursor: "pointer" }}>
                <input type="checkbox" checked={bedtimeNotificationEnabled} onChange={e => onBedtimeNotificationEnabled(e.target.checked)} />
                Bedtime summary
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 130, fontSize: 13, color: "var(--ink-mid)" }}>Send each evening at</span>
                <input type="time" className="modal-input" value={bedtimeNotificationTime} onChange={e => onBedtimeNotificationTime(e.target.value)} />
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                className="modal-save"
                onClick={onRequestNotificationPermission}
                style={{ flex: "none", minWidth: 172, padding: "12px 18px" }}
              >
                {notificationPermission === "granted" ? "✅ Notifications allowed" : "Allow notifications"}
              </button>
              <div style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.4 }}>
                {notificationPermission === "granted"
                  ? "Notifications are enabled. Summaries fire via the background service."
                  : "Tap to allow morning and bedtime summaries."}
              </div>
            </div>
            {notificationPermission !== "granted" && (
              <div style={{ marginTop: 14, padding: "10px 12px", background: "rgba(60,40,20,0.06)", borderRadius: 6, fontSize: 12, color: "var(--ink-mid)", lineHeight: 1.6 }}>
                <strong style={{ color: "var(--ink-dark)" }}>📱 For notifications on your phone:</strong><br />
                <strong>Android:</strong> Open in Chrome · tap ⋮ · "Add to Home Screen", then allow notifications.<br />
                <strong>iPhone (iOS 16.4+):</strong> Open in Safari · tap Share · "Add to Home Screen", open the installed app, then allow notifications here.
              </div>
            )}
          </>)}

          <div className="modal-actions" style={{ marginTop: 20 }}>
            <button className="modal-cancel" onClick={onClose}>Cancel</button>
            <button className="modal-save" onClick={saveAndClose}>Save Settings</button>
          </div>
          <div style={{ textAlign: "center", marginTop: 16, fontSize: 11 }}>
            <a href="https://flofax.autom8sustain.com/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: "var(--ink-mid)", opacity: 0.6, textDecoration: "underline", margin: "0 8px", pointerEvents: "auto" }}>Privacy Policy</a>
            <span style={{ color: "var(--ink-mid)", opacity: 0.4 }}>•</span>
            <a href="https://flofax.autom8sustain.com/terms-of-service.html" target="_blank" rel="noopener noreferrer" style={{ color: "var(--ink-mid)", opacity: 0.6, textDecoration: "underline", margin: "0 8px", pointerEvents: "auto" }}>Terms of Service</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
// ─── Utility: fetch events across calendars for arbitrary range
async function fetchEventsInRange(start, end) {
  if (!window.gapi?.client) return [];
  const all = [];
  try {
    const calListRes = await window.gapi.client.calendar.calendarList.list();
    const cals = (calListRes.result.items || []).map(c => c.id);
    await Promise.all(cals.map(async calId => {
      try {
        const res = await window.gapi.client.calendar.events.list({
          calendarId: calId, singleEvents: true, orderBy: "startTime", maxResults: 500,
          timeMin: start.toISOString(), timeMax: end.toISOString(),
        });
        (res.result.items || []).forEach(it => { it._calendarId = calId; all.push(it); });
      } catch (e) { /* per-calendar ignore */ }
    }));
    all.sort((a, b) => new Date((a.start?.dateTime || a.start?.date)).getTime() - new Date((b.start?.dateTime || b.start?.date)).getTime());
  } catch (e) { console.error(e); }
  return all;
}

function EventsListModal({ start, end, events, onClose }) {
  function formatDateLong(iso) { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet" style={{ maxWidth: 540 }}>
        <div className="modal-content">
          <div className="modal-handle" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div className="modal-title">Events</div>
              <div className="modal-sub">{start && end ? `${formatDateLong(start)} — ${formatDateLong(end)}` : ''}</div>
            </div>
            <button className="modal-close-btn" onClick={onClose}>✕</button>
          </div>
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {events.length === 0 ? <div style={{ padding: 12, fontStyle: 'italic' }}>No events in this range</div> : (
              events.map((ev, i) => {
                const s = ev.start?.dateTime || ev.start?.date; const t = ev.start?.dateTime ? new Date(ev.start.dateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : (ev.start?.date ? 'All day' : '');
                const { clean, phone, email, postcode, time } = parseEventText(ev.summary);
                const isAndroid = /Android/i.test(navigator.userAgent);
                const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                return (<div key={i} style={{ padding: 10, borderBottom: '1px solid rgba(60,40,20,0.06)', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{clean || '(No title)'}</span>
                    {time && (
                      <a
                        className="event-action-icon"
                        href={isAndroid ? `intent://#Intent;action=android.intent.action.SET_ALARM;i.android.intent.extra.alarm.HOUR=${time.hour};i.android.intent.extra.alarm.MINUTES=${time.minute};S.android.intent.extra.alarm.MESSAGE=${encodeURIComponent(clean)};end` : (isIOS ? "clock-alarm://" : "#")}
                        onClick={e => {
                          e.stopPropagation();
                          if (!isAndroid && !isIOS) {
                            e.preventDefault();
                            alert(`Alarm setting helper:\nSet an alarm on your phone for ${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')} - "${clean}"`);
                          }
                        }}
                        title={`Set alarm for ${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`}
                        target={isAndroid ? "_self" : "_blank"}
                        rel="noreferrer"
                        style={{ fontSize: 13 }}
                      >
                        ⏰
                      </a>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-mid)' }}>{s ? `${formatDateLong(s)} ${t ? ' · ' + t : ''}` : ''}</div>
                </div>);
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TasksPanel({ onClose, tokenClient }) {
  const [taskLists, setTaskLists] = useState([]);
  const [activeListId, setActiveListId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [newText, setNewText] = useState("");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskTitle, setEditTaskTitle] = useState("");

  useEffect(() => { loadLists(); }, []);

  async function ensureTasksApi() {
    if (!window.gapi?.client) return false;
    if (!window.gapi.client.tasks) {
      try {
        await window.gapi.client.load('tasks', 'v1');
      } catch (e) {
        console.error('Failed to load Tasks API', e);
        return false;
      }
    }
    return true;
  }

  async function loadLists() {
    try {
      if (!await ensureTasksApi()) return;
      const res = await window.gapi.client.tasks.tasklists.list();
      const lists = res.result.items || [];
      setTaskLists(lists);
      if (lists[0]) { setActiveListId(lists[0].id); loadTasks(lists[0].id); }
    } catch (e) { console.error(e); }
  }

  async function loadTasks(listId) {
    try {
      if (!await ensureTasksApi()) return;
      const res = await window.gapi.client.tasks.tasks.list({ tasklist: listId, showCompleted: true, showHidden: true, maxResults: 500 });
      setTasks(res.result.items || []);
    } catch (e) { console.error(e); }
  }

  async function addTask() {
    if (!newText.trim()) return;
    try {
      if (!await ensureTasksApi()) return;
      await window.gapi.client.tasks.tasks.insert({ tasklist: activeListId, resource: { title: newText.trim() } });
      setNewText(""); loadTasks(activeListId);
    } catch (e) { console.error(e); }
  }

  async function updateTaskText(t) {
    if (!editTaskTitle.trim()) { setEditingTaskId(null); return; }
    try {
      if (!await ensureTasksApi()) return;
      await window.gapi.client.tasks.tasks.update({ tasklist: activeListId, task: t.id, resource: { ...t, title: editTaskTitle.trim() } });
      setEditingTaskId(null);
      loadTasks(activeListId);
    } catch (e) { console.error(e); }
  }

  async function toggleComplete(t) {
    try {
      if (!await ensureTasksApi()) return;
      const patched = { ...t };
      if (t.status === 'completed') { patched.status = 'needsAction'; delete patched.completed; }
      else { patched.status = 'completed'; patched.completed = new Date().toISOString(); }
      await window.gapi.client.tasks.tasks.update({ tasklist: activeListId, task: t.id, resource: patched });
      loadTasks(activeListId);
    } catch (e) { console.error(e); }
  }

  async function deleteTask(t) {
    try {
      if (!await ensureTasksApi()) return;
      await window.gapi.client.tasks.tasks.delete({ tasklist: activeListId, task: t.id });
      loadTasks(activeListId);
    } catch (e) { console.error(e); }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet" style={{ maxWidth: 540 }}>
        <div className="modal-content">
          <div className="modal-handle" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div className="modal-title">Tasks</div>
              <div className="modal-sub">Tap to toggle complete · swipe to view completed</div>
            </div>
            <button className="modal-close-btn" onClick={onClose}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <select className="modal-input" value={activeListId || ""} onChange={e => { setActiveListId(e.target.value); loadTasks(e.target.value); }} style={{ flex: 1 }}>
              {taskLists.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
            </select>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="modal-tab" onClick={() => setShowCompleted(s => !s)}>{showCompleted ? 'Hide Completed' : 'Show Completed'}</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input className="modal-input" placeholder="Add task…" value={newText} onChange={e => setNewText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTask()} />
            <button className="modal-save" onClick={addTask}>Add</button>
          </div>
          <div style={{ maxHeight: '56vh', overflowY: 'auto' }}>
            {(tasks.filter(t => (showCompleted ? true : t.status !== 'completed')).length === 0) ? <div style={{ padding: 12, fontStyle: 'italic' }}>No tasks</div> : (
              tasks.filter(t => (showCompleted ? true : t.status !== 'completed')).map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, borderBottom: '1px solid rgba(60,40,20,0.06)' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {editingTaskId === t.id ? (
                      <input className="modal-input" style={{ fontSize: 14, padding: "4px 8px" }} value={editTaskTitle} onChange={e => setEditTaskTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && updateTaskText(t)} autoFocus />
                    ) : (
                      <div style={{ fontSize: 14, textDecoration: t.status === 'completed' ? 'line-through' : 'none', color: t.status === 'completed' ? 'rgba(60,40,20,0.5)' : 'var(--ink-dark)' }}>{t.title}</div>
                    )}
                    {t.notes && <div style={{ fontSize: 12, color: 'var(--ink-mid)' }}>{t.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {editingTaskId === t.id ? (
                      <button className="nav-btn" onClick={() => updateTaskText(t)} style={{ height: 32, fontSize: 14 }}>Save</button>
                    ) : (
                      <>
                        <button className="nav-btn" onClick={() => { setEditingTaskId(t.id); setEditTaskTitle(t.title); }} style={{ height: 32 }}>✏️</button>
                        <button className="nav-btn" onClick={() => toggleComplete(t)} style={{ height: 32 }}>{t.status === 'completed' ? '↶' : '✓'}</button>
                        <button className="nav-btn" onClick={() => deleteTask(t)} style={{ height: 32 }}>🗑</button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
const BASE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --ink-black:#1A1208;--ink-dark:#2C2010;--ink-mid:#4A3828;
  --ruling-line:rgba(60,40,20,0.12);--ruling-margin:rgba(180,60,40,0.25);
  --accent-red:#8B1A1A;--accent-gold:#B8860B;--accent-blue:#1A3A5C;
  --event-font:'EB Garamond',Georgia,serif;
}
html,body,#root{height:100%;width:100%;overflow:hidden;background:var(--leather-dark);
  font-family:'EB Garamond',Georgia,serif;-webkit-font-smoothing:antialiased;user-select:none;}
.app-shell{height:100dvh;display:flex;flex-direction:column;align-items:center;justify-content:center;
  background:radial-gradient(ellipse at 30% 20%,color-mix(in srgb,var(--leather-mid) 60%,transparent) 0%,transparent 60%),
    radial-gradient(ellipse at 70% 80%,color-mix(in srgb,var(--leather-dark) 80%,transparent) 0%,transparent 60%),
    var(--leather-dark);position:relative;}

/* ── Sign-in ── */
.signin-screen{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:32px;padding:40px 24px;text-align:center;}
.signin-logo{width:80px;height:80px;background:var(--leather-mid);border:3px solid var(--ring-gold);border-radius:50%;
  display:flex;align-items:center;justify-content:center;font-family:'Playfair Display',serif;font-size:32px;color:var(--ring-gold);
  box-shadow:0 4px 24px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.1);}
.signin-title{font-family:'Playfair Display',serif;font-size:28px;font-weight:700;color:var(--paper-cream);letter-spacing:0.02em;}
.signin-sub{font-size:15px;color:var(--leather-tan);letter-spacing:0.04em;line-height:1.6;}
.signin-btn{display:flex;align-items:center;gap:12px;padding:14px 28px;background:var(--paper-cream);border:none;border-radius:4px;
  font-family:'EB Garamond',serif;font-size:17px;color:var(--ink-dark);cursor:pointer;
  box-shadow:0 4px 16px rgba(0,0,0,0.4);transition:transform 0.15s;}
.signin-btn:active{transform:scale(0.97);}

/* ── Planner shell ── */
.planner-container{width:100%;height:100dvh;display:flex;flex-direction:column;position:relative;overflow:hidden;}
.top-bar{background:linear-gradient(180deg,var(--leather-mid) 0%,var(--leather-dark) 100%);border-bottom:2px solid var(--ring-gold);
  padding:8px 16px 6px;display:flex;align-items:center;justify-content:space-between;
  position:relative;z-index:10;box-shadow:0 2px 12px rgba(0,0,0,0.5);flex-shrink:0;}
.top-bar-title{font-family:'Playfair Display',serif;font-size:18px;font-weight:600;color:var(--ring-gold);
  letter-spacing:0.08em;text-transform:uppercase;text-shadow:0 1px 4px rgba(0,0,0,0.5);}
.top-bar-week{font-size:12px;color:var(--leather-tan);letter-spacing:0.06em;font-style:italic;}
.top-bar-actions{display:flex;gap:8px;align-items:center;}
.icon-btn{width:32px;height:32px;background:rgba(255,255,255,0.08);border:1px solid rgba(200,168,75,0.3);border-radius:50%;
  color:var(--ring-gold);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:16px;transition:all 0.2s;}
.icon-btn:active{background:rgba(200,168,75,0.2);transform:scale(0.92);}
.period-select{font-family:var(--event-font);font-size:13px;color:var(--paper-cream);background:rgba(255,255,255,0.08);border:1px solid rgba(200,168,75,0.4);border-radius:24px;padding:8px 12px;outline:none;cursor:pointer;transition:all 0.2s;}
.period-select option{background:var(--leather-dark);color:var(--paper-cream);}
.update-banner{display:flex;flex-direction:column;gap:10px;padding:12px 14px;margin:10px 16px 0;border:1px solid rgba(232,197,71,0.35);border-radius:12px;background:rgba(232,197,71,0.12);color:var(--ink-dark);font-size:13px;line-height:1.5;}
.update-actions{display:flex;gap:8px;justify-content:flex-end;}
.update-later,.update-now{padding:8px 14px;border-radius:6px;border:1px solid transparent;cursor:pointer;font-family:'EB Garamond',serif;font-size:13px;}
.update-later{background:rgba(255,255,255,0.88);color:var(--ink-dark);border-color:rgba(60,40,20,0.12);}
.update-later:hover{background:rgba(255,255,255,1);}
.update-now{background:var(--ring-gold);color:var(--leather-dark);border-color:rgba(232,197,71,0.6);}
.update-now:hover{background:rgb(232,179,32);}
.modal-close-btn{width:32px;height:32px;border:none;border-radius:50%;background:rgba(255,255,255,0.16);color:var(--ink-black);font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all 0.2s;border:1px solid rgba(200,168,75,0.3);}
.modal-close-btn:hover{background:rgba(232,197,71,0.18);color:var(--ring-gold);}

/* ── Page viewport ── */
.page-viewport{flex:1;position:relative;overflow:hidden;display:flex;min-height:0;}
.page-wrapper{position:absolute;inset:0;display:flex;align-items:stretch;padding:12px 10px 10px;
  transition:transform 0.32s cubic-bezier(0.25,0.46,0.45,0.94),opacity 0.32s ease;}
.page-wrapper.landscape{padding:8px 6px 6px;}
.page-wrapper.slide-exit-left{transform:translateX(-100%);opacity:0;}
.page-wrapper.slide-exit-right{transform:translateX(100%);opacity:0;}

/* ── Book spine ── */
.book-spine{width:20px;flex-shrink:0;
  background:linear-gradient(to right,rgba(0,0,0,0.35) 0%,var(--leather-mid) 30%,var(--leather-tan) 50%,var(--leather-mid) 70%,rgba(0,0,0,0.35) 100%);
  position:relative;z-index:5;box-shadow:inset -2px 0 6px rgba(0,0,0,0.4),inset 2px 0 6px rgba(0,0,0,0.4);}
.book-spine::before{content:'';position:absolute;top:0;bottom:0;left:50%;transform:translateX(-50%);
  width:2px;background:rgba(200,168,75,0.3);}

/* ── Flofax page ── */
.flofax-page{flex:1;display:flex;flex-direction:column;background:var(--paper-cream);
  border-radius:2px 4px 4px 2px;position:relative;overflow:hidden;
  box-shadow:4px 0 8px rgba(0,0,0,0.15),-2px 0 4px rgba(0,0,0,0.1),0 4px 16px rgba(0,0,0,0.3);}
.flofax-page.landscape-page{border-radius:2px;}
/* When a page contains exactly 4 vertical slots (summary + days or 4 days), make them equal height by default */
.flofax-page.four-grid .page-inner-content > .week-summary,
.flofax-page.four-grid .page-inner-content > .day-section {
  flex: 1 1 0; min-height: 0; display:flex; flex-direction:column;
}
.flofax-page.four-grid .day-events { overflow:auto; }

/* Binding holes */
.binding-holes{position:absolute;left:0;top:0;bottom:0;width:28px;display:flex;flex-direction:column;
  justify-content:space-around;padding:20px 0;z-index:5;pointer-events:none;}
.hole{width:16px;height:16px;background:var(--leather-dark);border-radius:50%;margin-left:6px;
  box-shadow:inset 0 2px 4px rgba(0,0,0,0.7);}
.page-tab{position:absolute;right:-2px;top:30%;width:18px;background:var(--leather-dark);
  border-radius:0 3px 3px 0;writing-mode:vertical-rl;font-family:'Playfair Display',serif;
  font-size:9px;font-weight:700;color:var(--ring-gold);padding:10px 3px;letter-spacing:0.15em;z-index:4;pointer-events:none;}

/* Left-hand page modifications for landscape layout */
.left-page .binding-holes{left:auto;right:0;}
.left-page .page-tab{right:auto;left:-2px;border-radius:3px 0 0 3px;}
.left-page .page-inner-content{padding:8px 52px 16px 52px;}
.turn-hint{position:absolute;top:50%;transform:translateY(-50%);right:0;width:24px;height:80px;
  display:flex;align-items:center;justify-content:center;
  background:linear-gradient(to right,transparent,rgba(200,168,75,0.12));pointer-events:none;z-index:3;}
.turn-hint-arrow{font-size:16px;color:rgba(200,168,75,0.45);animation:arrowBob 2s ease-in-out infinite;}
@keyframes arrowBob{0%,100%{opacity:0.4;transform:translateX(0);}50%{opacity:0.9;transform:translateX(3px);}}

/* ── SCROLL CONTAINER — rules scroll with content ── */
.page-scroll-container{
  flex:1;min-height:0;
  position:relative;
  overflow-y:auto;
  -webkit-overflow-scrolling:touch;
}
/* Ruled lines rendered INSIDE scroll container so they scroll with content */
.ruled-lines-overlay{
  position:absolute;top:0;left:0;right:0;
  /* tall enough to cover any amount of content */
  height:max(100%,4000px);
  background-image:repeating-linear-gradient(
    transparent,transparent 27px,
    var(--ruling-line) 27px,var(--ruling-line) 28px);
  background-size:100% 28px;
  pointer-events:none;z-index:0;
}
.margin-line-overlay{
  position:absolute;top:0;bottom:0;left:44px;width:1px;
  background:var(--ruling-margin);
  pointer-events:none;z-index:1;
  /* make it tall too */
  height:max(100%,4000px);
}
.page-inner-content{
  position:relative;z-index:2;
  padding:8px 12px 16px 52px;
  display:flex;flex-direction:column;
  min-height:100%;
  font-family:var(--event-font);
}

/* ── Week summary ── */
.week-summary{flex:0 0 auto;padding:8px 0 6px;border-bottom:1.5px solid rgba(60,40,20,0.18);margin-bottom:2px;}
.section-label{font-family:'Playfair Display',serif;font-size:10px;font-weight:700;
  color:var(--accent-red);text-transform:uppercase;letter-spacing:0.14em;margin-bottom:5px;}
.week-summary-title{font-family:'Playfair Display',serif;font-size:14px;font-weight:600;font-style:italic;
  color:var(--ink-black);margin-bottom:4px;}
.week-summary-pills{display:flex;flex-wrap:wrap;gap:3px;}
.week-pill{display:flex;align-items:center;gap:4px;background:rgba(180,100,40,0.08);
  border:1px solid rgba(180,100,40,0.2);border-radius:20px;padding:2px 8px;
  font-size:10px;color:var(--ink-mid);}
.week-pill-more{cursor:pointer;transition:background 0.2s,color 0.2s;}
.week-pill-more:hover{background:rgba(232,197,71,0.18);color:var(--ink-dark);}
.week-pill-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0;}
.week-empty{font-size:12px;color:rgba(60,40,20,0.3);font-style:italic;}

/* ── Day section ── */
.day-section{flex:0 0 auto;padding:5px 0 3px;border-bottom:1px dashed rgba(60,40,20,0.14);}
.day-section.last{border-bottom:none;}
.day-header{display:flex;align-items:baseline;gap:8px;margin-bottom:4px;}
.day-name{font-family:'Playfair Display',serif;font-size:13px;font-weight:700;color:var(--ink-black);}
.day-name.today{color:var(--accent-red);}
.day-num{font-size:11px;color:var(--ink-mid);font-style:italic;}
.day-today-badge{font-size:9px;font-weight:700;background:var(--accent-red);color:white;
  border-radius:3px;padding:1px 5px;text-transform:uppercase;letter-spacing:0.1em;}
.day-add-btn{margin-left:auto;width:20px;height:20px;background:transparent;
  border:1px solid rgba(60,40,20,0.2);border-radius:50%;cursor:pointer;
  display:flex;align-items:center;justify-content:center;font-size:14px;
  color:rgba(60,40,20,0.35);line-height:1;transition:all 0.15s;flex-shrink:0;}
.day-add-btn:active{background:rgba(200,168,75,0.15);color:var(--accent-red);}
.day-events{display:flex;flex-direction:column;gap:2px;min-height:36px;}
.event-entry{display:flex;align-items:flex-start;gap:5px;min-height:20px;
  cursor:pointer;border-radius:2px;padding:1px 2px;transition:background 0.12s;}
.event-entry:active{background:rgba(200,168,75,0.12);}
.event-bullet{width:3px;flex-shrink:0;align-self:stretch;border-radius:2px;margin-top:2px;min-height:14px;}
.event-time{font-size:9px;color:var(--ink-mid);white-space:nowrap;flex-shrink:0;margin-top:2px;font-style:italic;min-width:32px;}
.event-title{font-size:11px;color:var(--ink-dark);line-height:1.4;flex:1;font-family:var(--event-font);}
.event-icon{margin-left:3px;font-size:11px;}
.event-action-icon{margin-left:3px;font-size:11px;text-decoration:none;cursor:pointer;}
.day-empty-line{border-bottom:1px solid var(--ruling-line);margin:6px 0;opacity:0.5;}

/* ── Nav strip ── */
.nav-strip{display:flex;align-items:center;padding:8px 16px 10px;
  background:linear-gradient(180deg,var(--leather-dark) 0%,var(--leather-mid) 100%);
  border-top:2px solid var(--ring-gold);gap:8px;flex-shrink:0;box-shadow:0 -2px 12px rgba(0,0,0,0.4);}
.landscape-nav{padding:6px 12px 8px;}
.nav-btn{height:36px;flex:0 0 auto;background:rgba(255,255,255,0.07);border:1px solid rgba(200,168,75,0.35);
  border-radius:3px;color:var(--ring-gold);cursor:pointer;display:flex;align-items:center;
  justify-content:center;font-size:18px;padding:0 14px;font-family:'EB Garamond',serif;transition:all 0.15s;}
.nav-btn:active{background:rgba(200,168,75,0.18);transform:scale(0.95);}
.nav-center{flex:1;text-align:center;}
.nav-week-label{font-family:'Playfair Display',serif;font-size:13px;font-weight:600;font-style:italic;
  color:var(--paper-cream);}
.nav-page-dots{display:flex;gap:5px;justify-content:center;margin-top:2px;}
.nav-dot{width:5px;height:5px;border-radius:50%;background:rgba(200,168,75,0.35);transition:all 0.2s;}
.nav-dot.active{background:var(--ring-gold);transform:scale(1.3);}
.nav-add-btn{height:36px;padding:0 16px;background:var(--accent-red);border:1px solid rgba(200,168,75,0.4);
  border-radius:3px;cursor:pointer;color:var(--paper-cream);font-family:'EB Garamond',serif;font-size:15px;
  letter-spacing:0.04em;display:flex;align-items:center;gap:6px;transition:all 0.15s;white-space:nowrap;}
.nav-add-btn:active{transform:scale(0.95);opacity:0.9;}

/* ── Loading ── */
.loading-overlay{position:absolute;inset:0;background:rgba(245,240,232,0.8);
  display:flex;align-items:center;justify-content:center;z-index:10;
  font-family:'Playfair Display',serif;font-style:italic;color:var(--ink-mid);font-size:15px;gap:10px;}

/* ── Modals ── */
.modal-overlay{position:fixed;inset:0;background:rgba(10,6,2,0.82);backdrop-filter:blur(4px);z-index:100000;
  display:flex;align-items:flex-end;justify-content:center;animation:fadeIn 0.2s ease;}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
.modal-sheet{width:100%;max-width:480px;background:var(--paper-aged);border-radius:16px 16px 0 0;
  padding:20px 20px 32px;animation:slideUp 0.3s cubic-bezier(0.25,0.46,0.45,0.94);
  border-top:3px solid var(--ring-gold);position:relative;overflow:hidden;max-height:88vh;overflow-y:auto;z-index:100001;}
.modal-content{position:relative;z-index:100002;}

@keyframes slideUp{from{transform:translateY(100%);}to{transform:translateY(0);}}
.modal-sheet{width:100%;max-width:480px;background:var(--paper-aged);border-radius:16px 16px 0 0;
  padding:20px 20px 32px;animation:slideUp 0.3s cubic-bezier(0.25,0.46,0.45,0.94);
  border-top:3px solid var(--ring-gold);position:relative;overflow:hidden;max-height:88vh;overflow-y:auto;}
.modal-sheet::after{content:'';position:absolute;inset:0;pointer-events:none;z-index:0;
  background-image:repeating-linear-gradient(transparent,transparent 27px,var(--ruling-line) 27px,var(--ruling-line) 28px);}
.modal-content{position:relative;z-index:1;}
.modal-handle{width:40px;height:4px;background:rgba(60,40,20,0.25);border-radius:2px;margin:0 auto 16px;}
.modal-title{font-family:'Playfair Display',serif;font-size:20px;font-weight:700;color:var(--ink-black);margin-bottom:4px;}
.modal-sub{font-size:13px;color:var(--ink-mid);font-style:italic;margin-bottom:18px;}
.modal-tabs{display:flex;border:1px solid rgba(60,40,20,0.2);border-radius:4px;overflow:hidden;margin-bottom:16px;}
.modal-tab{flex:1;padding:9px;background:transparent;border:none;cursor:pointer;font-family:'EB Garamond',serif;
  font-size:14px;color:var(--ink-mid);transition:all 0.2s;display:flex;align-items:center;justify-content:center;gap:6px;}
.modal-tab.active{background:var(--leather-dark);color:var(--ring-gold);}
.modal-field{margin-bottom:12px;}
.modal-label{font-size:11px;font-weight:700;color:var(--accent-red);text-transform:uppercase;letter-spacing:0.1em;margin-bottom:4px;}
.modal-input{width:100%;background:rgba(255,255,255,0.7);border:1px solid rgba(60,40,20,0.2);border-radius:3px;
  padding:10px 12px;font-family:'EB Garamond',serif;font-size:15px;color:var(--ink-dark);outline:none;transition:border-color 0.15s;}
.modal-input:focus{border-color:var(--accent-red);}
.modal-row{display:flex;gap:10px;}.modal-row .modal-field{flex:1;}
.modal-actions{display:flex;gap:8px;margin-top:18px;flex-wrap:wrap;}
.modal-cancel{flex:1;padding:12px;background:transparent;border:1px solid rgba(60,40,20,0.25);border-radius:3px;
  cursor:pointer;font-family:'EB Garamond',serif;font-size:15px;color:var(--ink-mid);}
.modal-cancel:active{background:rgba(60,40,20,0.06);}
.modal-save{flex:2;padding:12px;background:var(--leather-dark);border:1px solid var(--ring-gold);
  border-radius:3px;cursor:pointer;font-family:'Playfair Display',serif;font-size:15px;
  color:var(--ring-gold);letter-spacing:0.05em;transition:all 0.15s;}
.modal-save:active{opacity:0.85;transform:scale(0.98);}
.modal-save:disabled{opacity:0.5;cursor:not-allowed;}
.voice-area{display:flex;flex-direction:column;align-items:center;gap:16px;padding:12px 0;}
.voice-btn{width:72px;height:72px;border-radius:50%;background:var(--accent-red);border:3px solid rgba(200,168,75,0.4);
  display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:30px;
  box-shadow:0 4px 24px rgba(139,26,26,0.5);transition:all 0.15s;}
.voice-btn.listening{animation:pulse 1.2s ease-in-out infinite;background:#B22222;}
@keyframes pulse{0%,100%{box-shadow:0 4px 24px rgba(178,34,34,0.5),0 0 0 0 rgba(178,34,34,0.4);}
  50%{box-shadow:0 4px 24px rgba(178,34,34,0.5),0 0 0 16px rgba(178,34,34,0);}}
.voice-status{font-family:'Playfair Display',serif;font-size:14px;font-style:italic;color:var(--ink-mid);text-align:center;white-space:pre-line;}
.voice-transcript{width:100%;background:rgba(255,255,255,0.7);border:1px solid rgba(60,40,20,0.2);border-radius:3px;
  padding:10px 12px;font-family:'EB Garamond',serif;font-size:14px;color:var(--ink-dark);min-height:52px;font-style:italic;}
.saving-spinner{display:inline-block;width:14px;height:14px;border:2px solid rgba(200,168,75,0.3);
  border-top-color:var(--ring-gold);border-radius:50%;animation:spin 0.7s linear infinite;vertical-align:middle;margin-right:6px;}
@keyframes spin{to{transform:rotate(360deg);}}
`;
